import { decode } from "../../userConsent/js/cookie/consentDecoder";
import { getCookie } from "@trinitymirrordigital/chameleon-utilities";
import { triggerEvent } from "../../scriptsCore/js/dataLayerUtilities";
import { once } from "../../scriptsCore/js/once";
import core from "@trinitymirrordigital/chameleon-core/src/js/chameleon-core.js";
const events = core.events;

export const vendors = {
  // [id, isNonIAB]
  adyoulike: [259, false],
  facebook: [2, true],
  google: [755, false],
  iristv: [8, true],
  lotame: [95, false],
  ozone: [524, false],
  pubmatic: [76, false],
  teads: [132, false],
  contentsquare: [13, true],
};

export const purposes = {
  STORE_AND_ACCESS_INFORMATION_ON_A_DEVICE: "1",
  SELECT_BASIC_ADS: "2",
  CREATE_A_PERSONALISED_ADS_PROFILE: "3",
  SELECT_PERSONALISED_ADS: "4",
  CREATE_A_PERSONALISED_CONTENT_PROFILE: "5",
  SELECT_PERSONALISED_CONTENT: "6",
  MEASURE_AD_PERFORMANCE: "7",
  MEASURE_CONTENT_PERFORMANCE: "8",
  APPLY_MARKET_RESEARCH_TO_GENERATE_AUDIENCE_INSIGHTS: "9",
  DEVELOP_AND_IMPROVE_PRODUCTS: "10",
  USE_PRECISE_GEOLOCATION_DATA: "11",
  ACTIVELY_SCAN_DEVICE_CHARACTERISTICS_FOR_IDENTIFICATION: "12",
};

/**
 * Gets static consent data from cookies and writes it to window as
 * dataLayerUtilities.setInfo() pushes data to google tag which is a
 * a performance hit, so we need to split data going into google and not.
 * So we need to use a temporary variable until that.
 * gdprApplies flag here is passed from CMP, otherwise assumed to be true
 * if user has a cookie
 */
export function updateConsent(gdprApplies = null) {
  // update global consent object
  window.TMConsent = {
    ...(window.TMConsent || {}), // current consent object
    ...readCookies(), // cookie data
    api: {
      isConsented, // api functions
      purposesAccepted,
      publisherPurposesAccepted,
      vendors,
      purposes,
    },
  };
  if (gdprApplies != null && window.TMConsent.tcfv2consent) {
    window.TMConsent.tcfv2consent.gdprApplies = gdprApplies;
  }

  triggerEvent("staticConsent.ready"); // deprecated
  triggerEvent("staticConsent.updated");
  events.fire(document.body, "staticConsent.updated");

  // if gdprApplies is null here (runForceReconsent is just loaded),
  // we don't know if gdpr applies yet, we have to get it from CMP
  const outsideEU = window.TMConsent.tcfv2consent?.gdprApplies === false;
  const haveConsent = !!window.TMConsent.tcfv2consent;
  if (haveConsent || outsideEU) {
    if (once("consent")) {
      triggerEvent("staticConsent.obtained");
      events.fire(document.body, "staticConsent.obtained");
    }
  }
}

/**
 * Updates gdprApplies from CMP events as there's no other way to know it
 */
export function updateGdprApplies(gdprApplies = null) {
  if (gdprApplies != null) {
    window.TMConsent = window.TMConsent || {};
    window.TMConsent.tcfv2consent = window.TMConsent.tcfv2consent || {};
    window.TMConsent.tcfv2consent.gdprApplies = gdprApplies;
  }
}

/**
 * @returns static consent object
 */
export function getConsent() {
  return window.TMConsent || {};
}

/**
 * Function to check vendor consent
 */
export function isConsented(vendorId, noniab = false) {
  const consentData = getConsent();
  if (consentData.tcfv2consent?.gdprApplies === false) {
    return true;
  }
  if (noniab) {
    return !!consentData.noniabconsent?.consent?.[vendorId];
  }

  const allowedVendors = consentData.tcfv2consent?.vendor?.consents;
  return !!allowedVendors?.[vendorId];
}

/**
 * Function to check all reqired purposes accepted
 */
export function purposesAccepted(requiredPurposes) {
  const consentData = getConsent();
  const consents = consentData.tcfv2consent?.purpose?.consents || {};
  const consentedPurposes = Object.keys(consents);
  return requiredPurposes.every((purpose) =>
    consentedPurposes.includes(purpose)
  );
}

/**
 * Function to check all reqired publisher purposes accepted
 */
export function publisherPurposesAccepted(requiredPurposes) {
  const consentData = getConsent();
  const consents = consentData.tcfv2consent?.publisher?.consents || {};
  const consentedPurposes = Object.keys(consents);
  return requiredPurposes.every((purpose) =>
    consentedPurposes.includes(purpose)
  );
}
/**
 * Function to read consent from cookies
 */
function readCookies() {
  const cookies = {
    "euconsent-v2": "tcfv2consent",
    noniabvendorconsent: "noniabconsent",
    eupubconsent: "pubconsent",
    usprivacy: "usconsent",
    addtl_consent: "googleatp", // should be read after tcfv2 to extend it
  };

  const consentData = Object.keys(cookies).reduce((accu, cookieName) => {
    // local storage value takes priority
    const cookieValue =
      window.localStorage.getItem(cookieName) || getCookie(cookieName);
    if (cookieValue) {
      const path = cookies[cookieName];
      const cookieData = decode(cookieName, cookieValue);
      if (cookieData) {
        // add decoded data to consent object
        cookieData.cookieContent = cookieValue;
        accu[path] = cookieData;

        // extend TCFv2 TCData with vaild Google ATP if both present
        if (cookieName === "addtl_consent" && accu.tcfv2consent) {
          accu.tcfv2consent.addtlConsent = cookieData.ac;
        }
      }
    }
    return accu;
  }, {});

  return consentData;
}
